import React, {
  useState,
  useEffect
} from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link as RouterLink,
} from "react-router-dom";

import moment from 'moment';

import { makeStyles } from '@material-ui/core/styles';
import {
  Chip,
  Avatar,
  Paper,
  Breadcrumbs,
  Link,
  Grid,
  Typography,
  Button,
  IconButton,
  Divider,
  Collapse,
  ListSubheader,
} from '@material-ui/core';
import {
  Skeleton
} from '@material-ui/lab';
import Alert from '@material-ui/lab/Alert';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import EditIcon from '@material-ui/icons/Edit';

import localization from '../utils/localizations';
import {
  IssueReportPanel,
  ThesisSecondSeminarDetail,
  ThesisSecondSeminarForm,
  StatusTimeline,
  DateTimeView,
  FileCommentsHistory,
  AdminNotes,
} from '../views';

import { useStatus } from '../data/Status';
import filterDocumentBy from '../utils/filterDocumentBy';
import grabAllDocuments from '../utils/grabAllDocuments';
import {
  Document
} from '../types';

const appConfig = (window as any).APP_CONFIG;


const useStyles = makeStyles((theme) => ({
  mainBody: {
    padding: 10,
  },
  secondPanel: {
    padding: 10,
    marginTop: 20,
    marginBottom: 20,
  },
  pageTitle: {
    marginTop: 10,
    marginBottom: 10,
  },
  breadcrumbs: {
    margin: 10,
  },
  mainGrid: {
    marginTop: 20,
  },
  gridLabel: {
    textAlign: 'right',
    color: '#555555',
  },
  gridLabelStandard: {
    textAlign: 'right',
  },
  gridLabelBolder: {
    textAlign: 'right',
    fontWeight: 500,
  },
  alignRight: {
    textAlign: 'right',
  },
  ugbOpenMessage: {
    marginTop: 20,
    marginBottom: 20,
  },
  marginRight: {
    marginRight: 10
  }
}));


function SecondSeminarPage() {
  const classes = useStyles();

  const [status, statusLoading, loadStatus] = useStatus();
  const [showForm, setShowForm] = useState(false);
  let allDocuments: Document[] = [];

  let currentData = undefined;
  let editButtonLabel = `${localization.submit} ${localization.secondSeminar}`;
  if (status?.second_seminar?.id) {
    currentData = {
      seminar_admission_letter: filterDocumentBy(status.second_seminar.documents, {kind: 'Surat Pengajuan Seminar'}),
      advisor_approval: filterDocumentBy(status.first_seminar.documents, {kind: 'Persetujuan Pembimbing'}),
      thesis_docx: filterDocumentBy(status.second_seminar.documents, {kind: 'Thesis', content_type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"}),
      thesis_pdf: filterDocumentBy(status.second_seminar.documents, {kind: 'Thesis', content_type: "application/pdf"}),
      paper_id_pdf: filterDocumentBy(status.second_seminar.documents, {kind: 'Makalah - Bahasa Indonesia', content_type: "application/pdf"}),
      paper_en_pdf: filterDocumentBy(status.second_seminar.documents, {kind: 'Makalah - English', content_type: "application/pdf"}),
      ksm: filterDocumentBy(status.second_seminar.documents, {kind: 'KSM'}),
    }
    if ((status.second_seminar.status == 'submitted') || (status.second_seminar.status == 'scheduled') || (status.second_seminar.status == 'completed') || (status.second_seminar.status == 'canceled')) {
      editButtonLabel = localization.updateData;
    }
    allDocuments = grabAllDocuments(status.second_seminar.documents, status.second_seminar.status_history);
  }

  let disableEdit = !(status?.second_seminar);
  let submissionDeadlineReached = false;
  let deadline = status?.current_ugb_period?.second_seminar_end;
  if (deadline) {
    if (moment() > moment(deadline)) {
      disableEdit = true;
      submissionDeadlineReached = true;
    }
  }

  if (!deadline) {
    disableEdit = true;
  }

  return (
    <>
    <Breadcrumbs aria-label="breadcrumb" className={classes.breadcrumbs}>
      <Link color="inherit" to={`${appConfig.homepage}`} component={RouterLink}>
        {localization.home}
      </Link>
      <Typography color="textPrimary">{localization.secondSeminar}</Typography>
    </Breadcrumbs>

    <Paper className={classes.mainBody}>
      <ListSubheader style={{paddingLeft: 8}}>{localization.secondSeminarPageTitle}</ListSubheader>

      <Collapse
        in={!showForm}
      >
        {!status.success ? (
          <div style={{position: 'relative', padding: 10}}>
            <Skeleton variant="rect" width={'100%'} height={20} style={{marginBottom: 10}}/>
            <Skeleton variant="rect" width={'100%'} height={20} style={{marginBottom: 40}}/>
            <Skeleton variant="rect" width={'30%'} height={20} style={{display: 'inline-block', marginBottom: 20, marginRight: 20}}/>
            <Skeleton variant="rect" width={'60%'} height={20} style={{display: 'inline-block', marginBottom: 20}}/>
            <Skeleton variant="rect" width={'30%'} height={20} style={{display: 'inline-block', marginBottom: 20, marginRight: 20}}/>
            <Skeleton variant="rect" width={'60%'} height={20} style={{display: 'inline-block', marginBottom: 20}}/>
            <Skeleton variant="rect" width={'30%'} height={20} style={{display: 'inline-block', marginBottom: 20, marginRight: 20}}/>
            <Skeleton variant="rect" width={'60%'} height={20} style={{display: 'inline-block', marginBottom: 20}}/>
          </div>
        ) : (
          submissionDeadlineReached ?
          (<Alert
            severity="error"
            className={classes.ugbOpenMessage}
          >
            {localization.submissionHasBeenClosedOn} <DateTimeView value={deadline} format={"Do MMMM YYYY HH:mm:ss"} /> (<DateTimeView value={deadline} fromNow />)
          </Alert>)
          :
          (!!deadline ? <Alert
            severity="info"
            className={classes.ugbOpenMessage}
          >
            {localization.secondSeminarSubmissionOpenUntil} <DateTimeView value={deadline} format={"Do MMMM YYYY HH:mm:ss"} /> (<DateTimeView value={deadline} fromNow />)
          </Alert>:
          <Alert
            severity="info"
            className={classes.ugbOpenMessage}
          >
            {localization.secondSeminarSubmissionNotOpen}
          </Alert>)
        )}

        {status && status.second_seminar && status.second_seminar.status &&
          <ThesisSecondSeminarDetail secondSeminar={status.second_seminar} />
        }

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon/>}
              onClick={(e: any) => {
                setShowForm(true);
              }}
              disabled={disableEdit}
            >{editButtonLabel}</Button>
          </Grid>
          <Grid item xs={6} className={classes.alignRight}>
            <Button
              variant="contained"
              to={`${appConfig.homepage}`} component={RouterLink}
            >{localization.backToHome}</Button>
          </Grid>
        </Grid>
      </Collapse>

      <Collapse
        in={showForm}
        {...(showForm ? { timeout: 1000 } : {})}
      >
        <ThesisSecondSeminarForm
          onSubmitted={() => {
            setShowForm(false);
            loadStatus();
          }}
          onCancelled={() => {
            setShowForm(false);
          }}
          values={currentData}
        />
      </Collapse>
    </Paper>

    {(status?.second_seminar?.notes?.length > 0) &&
      <AdminNotes notes={status.second_seminar.notes} />
    }

    {status && status.second_seminar && (allDocuments.length > 0) && <FileCommentsHistory documents={allDocuments} /> }
    {status && status.second_seminar && <StatusTimeline history={status.second_seminar.status_history} /> }

    <IssueReportPanel />
    </>
  );
}

export default SecondSeminarPage;